import {
  Box,
  Button,
  Field,
  Flex,
  Label,
  LabelVariant,
  Link,
  Select,
  Text,
  toast,
} from '@workwhile/ui'
import {
  DirectDepositStatus,
  EvereeProfileResponse,
  FederalTaxWithholdingStatus,
  TinVerificationStatus,
  UserOutstandingBalance,
  W2PayrollFrequency,
} from 'api/payments'
import { User } from 'api/typings'
import { toTitleCase } from 'lib/string'
import { useUpdateUserPaymentProfile } from 'queries/payments/useUserPaymentProfile'
import React, { RefObject, useState } from 'react'
import { AmountEntry } from './AmountEntry'
import { BankAccountBox } from './BankAccountBox'

interface EvereeBoxProps {
  user: User
  evereeProfile: EvereeProfileResponse | null
  w2PayrollFrequency: W2PayrollFrequency
  w2GarnishmentsNeeded: boolean
  outstandingBalance: UserOutstandingBalance
  evereeBoxRef: RefObject<HTMLDivElement | null>
}

const getFederalTaxWithholdingStatusVariant = (
  status: FederalTaxWithholdingStatus | null
): LabelVariant => {
  if (!status) return 'neutral'
  switch (status.toLowerCase()) {
    case 'processed':
      return 'success'
    case 'submitted':
      return 'information'
    case 'none':
    default:
      return 'neutral'
  }
}

const getDirectDepositStatusVariant = (
  status: DirectDepositStatus | null
): LabelVariant => {
  if (!status) return 'neutral'
  switch (status.toLowerCase()) {
    case 'submitted':
      return 'success'
    case 'failed':
      return 'error'
    case 'none':
    default:
      return 'neutral'
  }
}

const getTinVerificationStatusVariant = (
  status: TinVerificationStatus | null
): LabelVariant => {
  if (!status) return 'neutral'
  switch (status.toLowerCase()) {
    case 'passed':
      return 'success'
    case 'failed':
      return 'error'
    case 'none':
    default:
      return 'neutral'
  }
}

const PAYROLL_FREQUENCY_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
]

const GARNISHMENTS_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
]

export const EvereeBox = ({
  user,
  evereeProfile,
  w2PayrollFrequency,
  w2GarnishmentsNeeded,
  outstandingBalance,
  evereeBoxRef,
}: EvereeBoxProps) => {
  const [payrollFrequency, setPayrollFrequency] =
    useState<W2PayrollFrequency | null>(w2PayrollFrequency)
  const [garnishmentsNeeded, setGarnishmentsNeeded] = useState<boolean | null>(
    w2GarnishmentsNeeded
  )
  const [submitting, setSubmitting] = useState(false)
  const updateProfileMutation = useUpdateUserPaymentProfile()

  const evereeLink = evereeProfile?.accountId
    ? `https://app.everee.com/workers/details/${user.w2EvereeId}?tab=PAYMENTS`
    : null

  if (!evereeProfile)
    return (
      <Box
        ref={evereeBoxRef}
        position="relative"
        bg="white"
        border="1px solid"
        borderColor="border"
        borderRadius="medium"
        p={3}
        minWidth="250px"
        maxWidth="100%"
        width="100%"
        boxShadow="small"
        zIndex={2}
      >
        <Flex alignItems="center" gap={2}>
          <Text fontWeight="bold" color="primary" fontSize={3}>
            No Everee profile
          </Text>
          <Label variant="primary">W2</Label>
        </Flex>
      </Box>
    )

  const bankAccounts = evereeProfile.externalBankAccounts.map((account) => ({
    ...account,
    last4: account.last4 ? account.last4.slice(-4) : null,
  }))

  const submitProfileUpdate = () => {
    if (!user.id) return
    setSubmitting(true)
    if (payrollFrequency === 'daily' && garnishmentsNeeded === true) {
      toast.error("We can't set daily payroll when workers have garnishments")
      return
    }
    updateProfileMutation.mutate(
      {
        userId: Number(user.id),
        w2PayrollFrequency: payrollFrequency,
        w2GarnishmentsNeeded: garnishmentsNeeded,
      },
      {
        onSuccess: () => {
          setSubmitting(false)
        },
        onError: () => {
          setSubmitting(false)
        },
      }
    )
  }

  return (
    <Box
      ref={evereeBoxRef}
      position="relative"
      bg="white"
      border="1px solid"
      borderColor="border"
      borderRadius="medium"
      p={3}
      minWidth="250px"
      maxWidth="100%"
      width="100%"
      boxShadow="small"
      zIndex={2}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Flex alignItems="center" gap={2}>
          <Text fontWeight="bold" color="primary" fontSize={3}>
            Everee
          </Text>
          <Label variant="primary">W2</Label>
          <Label variant="primary">{evereeProfile.version}</Label>
          <Label variant="warning">Current W2 Provider</Label>
        </Flex>
        {evereeLink ? (
          <Link to={evereeLink} target="_blank">
            {user.w2EvereeId?.slice(0, 15)}...
          </Link>
        ) : null}
      </Flex>
      <Box mb={2}>
        <Field label="Set W2 Payment Profile">
          <Flex flexDirection="column" gap={2}>
            <Flex gap={2} alignItems="center">
              <Box flex={1}>
                <Field label="Payroll Frequency">
                  <Select
                    name="newPayrollFrequency"
                    value={PAYROLL_FREQUENCY_OPTIONS.find(
                      (option) => option.value === payrollFrequency
                    )}
                    onChange={(option) => {
                      const { value } = option as { value: string }
                      setPayrollFrequency(value as W2PayrollFrequency)
                    }}
                    options={PAYROLL_FREQUENCY_OPTIONS}
                  />
                </Field>
              </Box>
              <Box flex={1}>
                <Field label="Garnishments Needed">
                  <Select
                    name="newGarnishmentsNeeded"
                    value={GARNISHMENTS_OPTIONS.find(
                      (option) => option.value === garnishmentsNeeded
                    )}
                    onChange={(option) => {
                      setGarnishmentsNeeded(option.value)
                    }}
                    options={GARNISHMENTS_OPTIONS}
                  />
                </Field>
              </Box>
              <Box>
                <Button
                  type="confirm"
                  disabled={!user.id}
                  loading={submitting}
                  onClick={submitProfileUpdate}
                >
                  Set Status
                </Button>
              </Box>
            </Flex>
          </Flex>
        </Field>
      </Box>
      <Flex
        flexDirection="row"
        gap={3}
        mb={2}
        alignItems="center"
        flexWrap="wrap"
      >
        <Flex>
          <Text fontWeight="bold" spacing={1}>
            Federal Withholding Status:
          </Text>
          <Label
            variant={getFederalTaxWithholdingStatusVariant(
              evereeProfile.federalTaxWithholdingStatus
            )}
            size="small"
          >
            {toTitleCase(evereeProfile.federalTaxWithholdingStatus || 'N/A')}
          </Label>
        </Flex>
        <Flex>
          <Text fontWeight="bold" spacing={1}>
            Direct Deposit:
          </Text>
          <Label
            variant={getDirectDepositStatusVariant(
              evereeProfile.directDepositStatus
            )}
            size="small"
          >
            {toTitleCase(evereeProfile.directDepositStatus || 'N/A')}
          </Label>
        </Flex>
        <Flex>
          <Text fontSize={1} fontWeight="bold" spacing={1}>
            TIN:
          </Text>
          <Label
            variant={getTinVerificationStatusVariant(
              evereeProfile.tinVerificationStatus
            )}
            size="small"
          >
            {toTitleCase(evereeProfile.tinVerificationStatus || 'N/A')}
          </Label>
        </Flex>
      </Flex>
      <Flex flexDirection="row" gap={3}>
        {/* Content Box */}
        <Box
          bg="background"
          border="1px solid"
          borderColor="border"
          borderRadius="small"
          p={3}
          flex={1}
        >
          <Text color="lightText" mb={2}>
            We delegate the payroll payouts to Everee.
            <br />
            Payroll is processed weekly on Monday and paid out by Thursday
          </Text>
          <AmountEntry
            ml={2}
            labelProps={{ color: 'lightText' }}
            label="Timesheets sent to Everee"
            value={outstandingBalance.earningsWithEveree}
          />
          <AmountEntry
            ml={2}
            labelProps={{ color: 'lightText', fontStyle: 'italic' }}
            label="(Unsent timesheets)"
            value={outstandingBalance.pendingEvereePayouts}
          />
        </Box>
        {/* Payout Options Box */}
        <Box
          bg="background"
          border="1px solid"
          borderColor="border"
          borderRadius="small"
          p={3}
          minWidth="200px"
          flex={1}
        >
          <Text fontWeight="bold" fontSize={2} mb={2}>
            Payout Options
          </Text>
          <Box>
            {bankAccounts.map((account, index) => (
              <BankAccountBox key={index} account={account} badges={[]} />
            ))}
            {bankAccounts.length === 0 && (
              <Text fontWeight="bold" color="error">
                No accounts for payout
              </Text>
            )}
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}
